import { Contact, Horse } from 'openapi';
import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { contactName } from 'utilities/Contact';

interface ReturnType {
  ownerBadgeElement: ReactNode;
}

interface Props {
  contacts: Contact[] | undefined;
  mares: Horse[] | undefined;
  mareUidFormValue: string | undefined;
}

/**
 * Small hook that shows a badge for the owner of the mare
 */
function useMareCycleOwnerBadge({ contacts, mares, mareUidFormValue }: Props): ReturnType {
  const { t } = useTranslation();

  /**
   * find the owner of the mare
   */
  const owners = useMemo(() => {
    if (!contacts || !mareUidFormValue || !mares) {
      return undefined;
    }
    const mare = mares.find(mare => mare.uid === mareUidFormValue);

    return contacts.filter(contact => mare?.owner_uids.includes(contact.uid));
  }, [contacts, mareUidFormValue, mares]);

  const ownerBadgeElement = useMemo(() => {
    if (owners && owners.length > 0) {
      return (
        <div className='mt-2 bg-gray-50 border text-sm px-3 py-2 rounded-md'>
          <strong>
            {owners.length > 1 ? t('owners', 'Owners') : t('owner', 'Owner')}
            {': '}
          </strong>{' '}
          {owners.map(owner => contactName(owner)).join(', ')}
        </div>
      );
    }
  }, [owners, t]);

  return {
    ownerBadgeElement,
  };
}

export default useMareCycleOwnerBadge;
